import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';
import clsx from 'clsx';
import { Button, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import type { ButtonProps, PopoverProps } from '@heroui/react';
import { RiEyeLine } from '@remixicon/react';

import { normalizeJsonInput } from '@/utils/json';

export interface JsonViewerProps extends React.HTMLAttributes<HTMLDivElement> {
  data: unknown;
  collapsed?: boolean | number;
  maxHeightPx?: number;
  enableClipboard?: boolean;
  theme?: string;
  unstyled?: boolean;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  collapsed = 2,
  maxHeightPx,
  enableClipboard = false,
  theme = 'google',
  unstyled = false,
  className,
  style,
  ...rest
}) => {
  const normalizedData = React.useMemo(() => normalizeJsonInput(data), [data]);
  const viewerData = React.useMemo(() => {
    if (Array.isArray(normalizedData)) {
      return { data: normalizedData };
    }

    if (normalizedData && typeof normalizedData === 'object') {
      return normalizedData as Record<string, unknown>;
    }

    return { value: normalizedData };
  }, [normalizedData]);

  const containerClass = unstyled
    ? clsx('overflow-auto rounded text-xs', className)
    : clsx(
        'border-default-200 bg-default-50 dark:bg-default-900/50 overflow-auto rounded border p-3 text-xs',
        className
      );

  return (
    <div className={containerClass} style={{ maxHeight: maxHeightPx, ...style }} {...rest}>
      <ReactJson
        src={viewerData}
        name={false}
        collapsed={collapsed}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard={enableClipboard}
        iconStyle="triangle"
        theme={theme as any}
      />
    </div>
  );
};

interface JsonViewerPopoverProps extends JsonViewerProps {
  title?: string;
  triggerLabel?: string;
  buttonProps?: Partial<ButtonProps>;
  popoverProps?: Partial<PopoverProps>;
  contentClassName?: string;
}

export const JsonViewerPopover: React.FC<JsonViewerPopoverProps> = ({
  data,
  title = 'Details',
  triggerLabel = 'View',
  buttonProps,
  popoverProps,
  contentClassName,
  className,
  maxHeightPx = 256,
  collapsed = 2,
  enableClipboard = true,
  ...viewerProps
}) => {
  const triggerContent = buttonProps?.children ?? triggerLabel;

  return (
    <Popover placement="bottom-start" {...popoverProps}>
      <PopoverTrigger>
        <Button
          size="sm"
          variant="light"
          color="primary"
          className={clsx('h-6 min-w-0 px-2', buttonProps?.className)}
          startContent={buttonProps?.startContent ?? <RiEyeLine className="h-3 w-3" />}
          {...buttonProps}
        >
          {triggerContent}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={clsx('w-96 max-w-[90vw]', contentClassName)}>
        <div className="px-1 py-2">
          <div className="text-small mb-2 font-bold">{title}</div>
          <JsonViewer
            data={data}
            collapsed={collapsed}
            maxHeightPx={maxHeightPx}
            enableClipboard={enableClipboard}
            unstyled
            className={clsx('max-h-64 overflow-auto rounded bg-default-50 p-2 dark:bg-default-900/50', className)}
            {...viewerProps}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

